import React, { useState } from 'react';
import { Device, EntityPathOption } from '../types';
import EntityPathField from './EntityPathField';
import FilterThresholdFields from './FilterThresholdFields';
import AssyDrawingPane from './AssyDrawingPane';
import { CLIP_ASSY_VARIANTS } from '../utils/clipAssy';
import { DECOMMISSIONED_EFFECT } from '../utils/lifecycleWording';
import Button from './ui/Button';
import { Input } from './pilot-ui/input';
import { Label } from './pilot-ui/label';
import { Checkbox } from './pilot-ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './pilot-ui/select';

/**
 * FS-1074 — the fields of a device's record, shared by the two editors: the
 * Device List Edit dialog and the device Settings tab. Each editor owns its
 * draft and its save; this renders the same fields, in the same order, with
 * the same help, in both. A field added here appears in both editors.
 *
 * Above the fields both editors show DeviceAppUserData. Tags are not here:
 * DeviceTagsPanel is their shared control, and it saves on its own; in the
 * device workspace they sit on the Overview tab (FS-501).
 */
interface DeviceRecordFieldsProps {
  draft: Device;
  onChange: (patch: Partial<Device>) => void;
  canWrite: boolean;
  /** The app holds this device's location; its address is edited there. */
  addressLocked: boolean;
  entityPathOptions: EntityPathOption[];
  entityPathOptionsError?: string | null;
  scopes: readonly string[];
}

type TextFieldName = 'name' | 'calibration_offset' | 'street_address' | 'city'
  | 'state' | 'postal_code' | 'latitude' | 'longitude';

const TEXT_FIELDS: { label: string; name: TextFieldName; type: 'text' | 'number'; help: string; appManaged?: boolean }[] = [
  { label: 'Name', name: 'name', type: 'text', help: 'The device serial used to identify this Clip; it cannot be edited here.' },
  { label: 'Calibration Offset', name: 'calibration_offset', type: 'number', help: 'Adjusts the pressure calibration offset used by this device.' },
  { label: 'Street Address', name: 'street_address', type: 'text', appManaged: true, help: 'Street address of the installation; app managed locations are edited in the app.' },
  { label: 'City', name: 'city', type: 'text', appManaged: true, help: 'City of the installation; app managed locations are edited in the app.' },
  { label: 'State', name: 'state', type: 'text', appManaged: true, help: 'State of the installation; app managed locations are edited in the app.' },
  { label: 'Postal Code', name: 'postal_code', type: 'text', appManaged: true, help: 'Postal code of the installation; app managed locations are edited in the app.' },
  { label: 'Latitude', name: 'latitude', type: 'number', help: 'Latitude of the installation in decimal degrees.' },
  { label: 'Longitude', name: 'longitude', type: 'number', help: 'Longitude of the installation in decimal degrees.' },
];

// FS-637: the server rejects '' for Assy, so "not recorded" is null; the
// dropdown needs a non-empty value to stand for it.
const ASSY_NOT_RECORDED = '__none__';
const ASSY_NOT_RECORDED_LABEL = '— not recorded —';

const TIME_ZONE_CHOICES = ['Eastern', 'Central', 'Mountain', 'Pacific'];
const timeZoneLabel = (value: string): string => TIME_ZONE_CHOICES.includes(value) ? value : `Current: ${value}`;

const DeviceRecordFields: React.FC<DeviceRecordFieldsProps> = ({
  draft, onChange, canWrite, addressLocked, entityPathOptions, entityPathOptionsError, scopes,
}) => {
  const [drawingOpen, setDrawingOpen] = useState(false);
  const disabled = !canWrite;

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    onChange({ [name]: type === 'number' ? (value === '' ? 0 : Number(value)) : value });
  };

  return (
    <div>
      {TEXT_FIELDS.map(({ label, name, type, help, appManaged }) => {
        const fieldDisabled = disabled || name === 'name' || (!!appManaged && addressLocked);
        return (
          <div className="mb-4" key={name}>
            <Label htmlFor={`setting-${name}`} className="mb-1">
              {label}:
              {appManaged && addressLocked && (
                <span className="text-xs text-muted-foreground ml-1">(managed in app)</span>
              )}
            </Label>
            <Input
              id={`setting-${name}`}
              aria-describedby={`setting-${name}-help`}
              type={type}
              name={name}
              value={String(draft[name] ?? '')}
              onChange={handleTextChange}
              className="w-full"
              disabled={fieldDisabled}
            />
            <p id={`setting-${name}-help`} className="mt-1 text-xs text-muted-foreground">{help}</p>
          </div>
        );
      })}

      {/* FS-637: dropdown only — the twelve V-1 Production variants, nothing
          typed. The "i" opens the assembly drawing so the operator can check
          which one they are looking at. */}
      <div className="mb-4">
        <Label htmlFor="setting-assy" className="mb-1">Assy:</Label>
        <div className="flex items-center gap-2">
          <Select
            name="assy"
            value={draft.assy ?? ASSY_NOT_RECORDED}
            onValueChange={(value) => {
              if (value === null) return;
              onChange({ assy: value === ASSY_NOT_RECORDED ? null : value });
            }}
            disabled={disabled}
          >
            <SelectTrigger id="setting-assy" className="w-full" title="Choose the device assembly variant" aria-describedby="setting-assy-help">
              <SelectValue>{draft.assy ?? ASSY_NOT_RECORDED_LABEL}</SelectValue>
            </SelectTrigger>
            <SelectContent listLabel="Assembly variant choices">
              <SelectItem value={ASSY_NOT_RECORDED}>{ASSY_NOT_RECORDED_LABEL}</SelectItem>
              {CLIP_ASSY_VARIANTS.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setDrawingOpen(true)}
            aria-label="Show the assembly drawing"
            title="Show the assembly drawing"
            className="h-7 w-7 shrink-0 rounded-full italic"
          >
            i
          </Button>
        </div>
        <p id="setting-assy-help" className="mt-1 text-xs text-muted-foreground">Choose the assembly variant. Open the drawing to compare the variants.</p>
      </div>

      <div className="mb-4">
        <Label htmlFor="setting-x46_fanside" className="mb-1">X46 Fanside:</Label>
        <Select
          name="x46_fanside"
          value={draft.x46_fanside ? 'true' : 'false'}
          onValueChange={(value) => onChange({ x46_fanside: value === 'true' })}
          disabled={disabled}
        >
          <SelectTrigger id="setting-x46_fanside" className="w-full" title="Set whether the X46 sensor is on the fan side" aria-describedby="setting-x46_fanside-help">
            <SelectValue>{draft.x46_fanside ? 'True' : 'False'}</SelectValue>
          </SelectTrigger>
          <SelectContent listLabel="X46 fan side choices"><SelectItem value="true">True</SelectItem><SelectItem value="false">False</SelectItem></SelectContent>
        </Select>
        <p id="setting-x46_fanside-help" className="mt-1 text-xs text-muted-foreground">True when the X46 pressure sensor is on the fan side of the filter, False when it is on the return side.</p>
      </div>

      <div className="mb-4">
        <Label htmlFor="setting-time_zone" className="mb-1">Time Zone:</Label>
        <Select
          name="time_zone"
          value={draft.time_zone}
          onValueChange={(value) => {
            if (!value) return;
            onChange({ time_zone: value });
          }}
          disabled={disabled}
        >
          <SelectTrigger id="setting-time_zone" className="w-full" title="Choose the device's local time zone" aria-describedby="setting-time_zone-help">
            <SelectValue>{draft.time_zone ? timeZoneLabel(draft.time_zone) : undefined}</SelectValue>
          </SelectTrigger>
          <SelectContent listLabel="Time zone choices">
            {draft.time_zone && !TIME_ZONE_CHOICES.includes(draft.time_zone) && <SelectItem value={draft.time_zone}>{timeZoneLabel(draft.time_zone)}</SelectItem>}
            {TIME_ZONE_CHOICES.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}
          </SelectContent>
        </Select>
        <p id="setting-time_zone-help" className="mt-1 text-xs text-muted-foreground">Sets the local time zone used for this device's readings.</p>
      </div>

      <div className="mb-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id="setting-decommissioned"
            checked={!!draft.decommissioned}
            onCheckedChange={(checked) => onChange({ decommissioned: checked })}
            disabled={disabled}
            title={DECOMMISSIONED_EFFECT}
            aria-describedby="setting-decommissioned-help"
          />
          <Label htmlFor="setting-decommissioned">Decommissioned</Label>
        </div>
        <p id="setting-decommissioned-help" className="text-xs text-muted-foreground mt-1">
          {DECOMMISSIONED_EFFECT}
        </p>
      </div>

      <FilterThresholdFields device={draft} disabled={disabled} onChange={onChange} />

      {canWrite && (
        <EntityPathField
          key={draft.id}
          value={draft.entity_path ?? null}
          options={entityPathOptions}
          scopes={scopes}
          optionsError={entityPathOptionsError}
          onChange={(path) => onChange({ entity_path: path })}
        />
      )}

      <AssyDrawingPane open={drawingOpen} onClose={() => setDrawingOpen(false)} />
    </div>
  );
};

export default DeviceRecordFields;
